import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search,
  FileText,
  MapPin,
  ChevronDown,
  Car,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Printer
} from 'lucide-react';
import Navigation from '../components/Navigation';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import BulkMVRInput from '../components/BulkMVRInput';
import BulkMVRResults from '../components/BulkMVRResults';
import { useAuth } from '../context/AuthContext';
import {
  searchMVR,
  MvrTitleResponse,
  MvrRegistrationResponse,
  MvrRecordType,
  MvrPricingResponse,
  formatMvrDate,
  getShortReportId,
  getErrorMessage,
  isStateSupported,
  fetchMvrPdfBlob,
  fetchMvrPricing,
  fetchSupportedStates,
  stateNames,
  StateCapabilities,
  transformStoredTitleResponse,
  transformStoredRegistrationResponse,
  BulkMvrItem,
  BulkMvrResponse,
  createBulkMvrReports,
  generateBulkMvrPdf
} from '../services/mvrService';
import { getMvrLogById } from '../services/searchHistoryService';
import { validateVINQuick, formatVehicleInfo, getVINErrorMessage, VINDecodeResult } from '../services/vinService';
import { getDashboardSettings } from '../services/settingsService';
import { getMyDppa } from '../services/clientService';
import { formatDppaUse } from '../utils/dppaUses';

// Recursively convert PascalCase keys to camelCase (handles legacy stored JSON from backend)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const normalizeToCamelCase = (obj: any): any => {
  if (obj === null || obj === undefined || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(normalizeToCamelCase);
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(obj)) {
    const camelKey = key.charAt(0).toLowerCase() + key.slice(1);
    result[camelKey] = normalizeToCamelCase(obj[key]);
  }
  return result;
};

// All state codes in alphabetical order (using stateNames imported from mvrService)
const allStateCodes = Object.keys(stateNames).sort((a, b) =>
  stateNames[a].localeCompare(stateNames[b])
);

// Session storage keys for persisting search results
const MVR_SESSION_KEYS = {
  VIN: 'mvr_search_vin',
  STATE: 'mvr_search_state',
  SEARCHED_VIN: 'mvr_searched_vin',
  SEARCHED_STATE: 'mvr_searched_state',
  RECORD_TYPE: 'mvr_record_type',
  TITLE_DATA: 'mvr_title_data',
  REGISTRATION_DATA: 'mvr_registration_data',
  VIN_DECODE_RESULT: 'mvr_vin_decode_result',
  MODE: 'mvr_search_mode',
  BULK_RESULTS: 'mvr_bulk_results',
};

// Helper functions for session storage
const loadFromSession = <T,>(key: string): T | null => {
  try {
    const stored = sessionStorage.getItem(key);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const saveToSession = (key: string, value: unknown): void => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Session storage might be full or disabled
  }
};

const clearMVRSession = (): void => {
  Object.values(MVR_SESSION_KEYS).forEach((key) => {
    sessionStorage.removeItem(key);
  });
};

interface CoverageData {
  nmvtis: {
    covered: string[];
    total: number;
  };
  dmv: {
    connected: string[];
    total: number;
  };
}

const MotorVehicleRecord: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isLoading } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Redirect if user doesn't have Motor Vehicle Record access
  useEffect(() => {
    if (isLoading) return;
    if (user?.canAccessMotorVehicleRecord === false) {
      navigate('/dashboard');
    }
  }, [user, isLoading, navigate]);

  // Load client DPPA code for display
  useEffect(() => {
    getMyDppa().then(setClientDppa).catch(() => {});
  }, []);

  // Form state
  const [vin, setVin] = useState(() => loadFromSession<string>(MVR_SESSION_KEYS.VIN) || '');
  const [selectedState, setSelectedState] = useState(() => loadFromSession<string>(MVR_SESSION_KEYS.STATE) || '');
  // Snapshot of the VIN/State that actually produced the displayed results. The report
  // is rendered against THIS, not the live inputs, so editing the VIN/State afterward
  // (without clicking Search) can't re-render the report with new labels + stale data.
  const [searchedVin, setSearchedVin] = useState(() => loadFromSession<string>(MVR_SESSION_KEYS.SEARCHED_VIN) || '');
  const [searchedState, setSearchedState] = useState(() => loadFromSession<string>(MVR_SESSION_KEYS.SEARCHED_STATE) || '');
  const [recordType, setRecordType] = useState<MvrRecordType>(() => loadFromSession<MvrRecordType>(MVR_SESSION_KEYS.RECORD_TYPE) || 'title');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showShortVinPrompt, setShowShortVinPrompt] = useState(false);
  // Something the state told us on a response that did NOT fail — e.g. Tennessee saying the
  // vehicle's current record is now held by Ohio. Kept apart from `error` on purpose: it is an
  // answer, and showing it in the red banner would read as a failure.
  const [notice, setNotice] = useState<string | null>(null);
  // The state the record moved to, as the API named it, plus its code. The code is what every
  // connectivity check and the state picker speak; the name is what the user reads.
  const [noticeState, setNoticeState] = useState<string | null>(null);
  const [noticeStateCode, setNoticeStateCode] = useState<string | null>(null);
  const [estimatedCost, setEstimatedCost] = useState<MvrPricingResponse | null>(null);
  const [clientDppa, setClientDppa] = useState<string | null>(null);
  const dppaDisplay = clientDppa ? formatDppaUse(clientDppa) : null;

  // Results state
  const [activeTab, setActiveTab] = useState<'title' | 'registration'>('title');
  const [titleData, setTitleData] = useState<MvrTitleResponse | null>(() =>
    loadFromSession<MvrTitleResponse>(MVR_SESSION_KEYS.TITLE_DATA)
  );
  const [registrationData, setRegistrationData] = useState<MvrRegistrationResponse | null>(() =>
    loadFromSession<MvrRegistrationResponse>(MVR_SESSION_KEYS.REGISTRATION_DATA)
  );

  // VIN validation state
  const [isValidatingVIN, setIsValidatingVIN] = useState(false);
  const [vinDecodeResult, setVinDecodeResult] = useState<VINDecodeResult | null>(() =>
    loadFromSession<VINDecodeResult>(MVR_SESSION_KEYS.VIN_DECODE_RESULT)
  );
  const [vinValidationError, setVinValidationError] = useState<string>('');

  // Coverage data
  const [coverageData, setCoverageData] = useState<CoverageData | null>(null);
  const [coverageLoading, setCoverageLoading] = useState(true);

  // Supported states from backend (dynamically fetched)
  const [supportedStates, setSupportedStates] = useState<StateCapabilities[]>([]);
  const [supportedStatesLoading, setSupportedStatesLoading] = useState(true);

  // Settings
  const [showPricing, setShowPricing] = useState(true);

  // Embedded PDF preview state
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
  const [pdfPreviewError, setPdfPreviewError] = useState<string>('');
  const pdfIframeRef = React.useRef<HTMLIFrameElement | null>(null);

  // Log loading state (for restoring previous results from activity page)
  const [isLoadingFromLog, setIsLoadingFromLog] = useState(false);
  const [logLoadError, setLogLoadError] = useState<string | null>(null);

  // Bulk mode state
  const [mode, setMode] = useState<'single' | 'bulk'>(() =>
    loadFromSession<'single' | 'bulk'>(MVR_SESSION_KEYS.MODE) || 'single'
  );
  const [bulkResults, setBulkResults] = useState<BulkMvrResponse | null>(() =>
    loadFromSession<BulkMvrResponse>(MVR_SESSION_KEYS.BULK_RESULTS)
  );
  const [isBulkSearching, setIsBulkSearching] = useState(false);
  const [isGeneratingBulkPdf, setIsGeneratingBulkPdf] = useState(false);
  const [bulkError, setBulkError] = useState('');

  // Persist state changes to sessionStorage
  useEffect(() => {
    saveToSession(MVR_SESSION_KEYS.VIN, vin);
  }, [vin]);

  useEffect(() => {
    saveToSession(MVR_SESSION_KEYS.STATE, selectedState);
  }, [selectedState]);

  useEffect(() => {
    saveToSession(MVR_SESSION_KEYS.SEARCHED_VIN, searchedVin);
  }, [searchedVin]);

  useEffect(() => {
    saveToSession(MVR_SESSION_KEYS.SEARCHED_STATE, searchedState);
  }, [searchedState]);

  useEffect(() => {
    saveToSession(MVR_SESSION_KEYS.RECORD_TYPE, recordType);
  }, [recordType]);

  useEffect(() => {
    if (titleData) saveToSession(MVR_SESSION_KEYS.TITLE_DATA, titleData);
  }, [titleData]);

  useEffect(() => {
    if (registrationData) saveToSession(MVR_SESSION_KEYS.REGISTRATION_DATA, registrationData);
  }, [registrationData]);

  useEffect(() => {
    if (vinDecodeResult) saveToSession(MVR_SESSION_KEYS.VIN_DECODE_RESULT, vinDecodeResult);
  }, [vinDecodeResult]);

  useEffect(() => {
    saveToSession(MVR_SESSION_KEYS.MODE, mode);
  }, [mode]);

  useEffect(() => {
    if (bulkResults) saveToSession(MVR_SESSION_KEYS.BULK_RESULTS, bulkResults);
  }, [bulkResults]);

  // For states that charge a single price or support combined search, the per-record-type toggle
  // is hidden. In those cases we still need to pick the right effective recordType from what the
  // state actually supports (e.g., Delaware is registration-only, so "both" would be misleading).
  // A combined-search state that prices title and registration separately (SC) still gets the
  // toggle: one call can return both, but the customer should not have to pay for both.
  const currentStateCapability = supportedStates.find(s => s.state === stateNames[selectedState]);
  const isCombinedState = estimatedCost?.hasSeparatePricing === false ||
    (!!currentStateCapability?.supportsCombinedSearch && currentStateCapability?.hasSeparatePricing !== true);
  const stateSupportsTitle = currentStateCapability?.supportsTitle ?? true;
  const stateSupportsRegistration = currentStateCapability?.supportsRegistration ?? true;
  // New Jersey sells registration only with the title: its registration search needs the plate the title returns.
  const registrationAloneOffered = currentStateCapability?.supportsRegistrationAlone !== false;
  const combinedLabel = stateSupportsTitle && stateSupportsRegistration
    ? 'Title & Registration'
    : stateSupportsTitle
      ? 'Title Only'
      : stateSupportsRegistration
        ? 'Registration Only'
        : 'Title & Registration';
  const effectiveRecordType: MvrRecordType = isCombinedState
    ? (stateSupportsTitle && stateSupportsRegistration
        ? 'both'
        : stateSupportsTitle
          ? 'title'
          : stateSupportsRegistration
            ? 'registration'
            : 'both')
    : recordType;

  // If the state doesn't support the currently-selected record type, flip to a supported one.
  // Covers the case where a user had "Registration" selected then chose a title-only state.
  useEffect(() => {
    if (!currentStateCapability) return;
    // A state reporting neither type has nothing valid to flip to: leave the choice alone, or the
    // rules below bounce title -> both -> registration -> both forever (SC, 2026-10-13).
    if (!stateSupportsTitle && !stateSupportsRegistration) return;
    if (recordType === 'title' && !stateSupportsTitle) {
      setRecordType(stateSupportsRegistration ? 'registration' : 'both');
    } else if (recordType === 'registration' && !stateSupportsRegistration) {
      setRecordType(stateSupportsTitle ? 'title' : 'both');
    } else if (recordType === 'registration' && !registrationAloneOffered) {
      setRecordType('both');
    } else if (recordType === 'both' && !(stateSupportsTitle && stateSupportsRegistration)) {
      setRecordType(stateSupportsTitle ? 'title' : 'registration');
    }
  }, [currentStateCapability, recordType, stateSupportsTitle, stateSupportsRegistration, registrationAloneOffered]);

  // Fetch estimated cost when state or record type changes
  useEffect(() => {
    if (!selectedState) {
      setEstimatedCost(null);
      return;
    }

    let cancelled = false;
    const loadPricing = async () => {
      try {
        const pricing = await fetchMvrPricing(selectedState, recordType);
        if (!cancelled) {
          setEstimatedCost(pricing);
        }
      } catch {
        if (!cancelled) setEstimatedCost(null);
      }
    };

    loadPricing();
    return () => { cancelled = true; };
  }, [selectedState, recordType, supportedStates]);

  // Fetch dashboard settings
  useEffect(() => {
    const fetchSettings = async () => {
      const settings = await getDashboardSettings();
      setShowPricing(settings.showPricing);
    };
    fetchSettings();
  }, []);

  // Fetch coverage data on mount
  useEffect(() => {
    const fetchCoverage = async () => {
      try {
        const response = await fetch('/api/coverage/states');
        const data = await response.json();
        setCoverageData(data);
      } catch (error) {
        console.error('Failed to fetch coverage data:', error);
      } finally {
        setCoverageLoading(false);
      }
    };

    fetchCoverage();
  }, []);

  // Fetch supported states from backend on mount
  useEffect(() => {
    const loadSupportedStates = async () => {
      try {
        const states = await fetchSupportedStates();
        setSupportedStates(states);
      } catch (error) {
        console.error('Failed to fetch supported states:', error);
      } finally {
        setSupportedStatesLoading(false);
      }
    };

    loadSupportedStates();
  }, []);

  // Body scroll lock when sidebar is open on mobile
  useEffect(() => {
    if (isSidebarOpen && window.innerWidth < 1024) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isSidebarOpen]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isLoading && !user) {
      navigate('/login');
    }
  }, [user, isLoading, navigate]);

  // Pre-fill VIN from URL query parameter (only if no logId - logId takes precedence)
  useEffect(() => {
    const vinParam = searchParams.get('vin');
    const logIdParam = searchParams.get('logId');
    // If logId is present, the log loading effect will handle setting VIN
    if (!logIdParam && vinParam && vinParam.length >= 5) {
      const cleanVin = vinParam.replace(/\s/g, '').toUpperCase();
      setVin(cleanVin);
      handleVINValidation(cleanVin);
    }
  }, [searchParams]);

  // Load previous MVR results from activity log (when navigating from activity page)
  useEffect(() => {
    const loadFromLog = async () => {
      const logIdParam = searchParams.get('logId');
      if (!logIdParam) return;

      setIsLoadingFromLog(true);
      setLogLoadError(null);
      // Clear session storage to prevent stale data from interfering
      clearMVRSession();
      // Clear any previous results and form state
      setTitleData(null);
      setRegistrationData(null);
      setVin('');
      setSelectedState('');
      setRecordType('both');

      try {
        const logDetail = await getMvrLogById(logIdParam);

        if (logDetail.status !== 'Success' || !logDetail.responseBody) {
          setLogLoadError('No data found for this record');
          return;
        }

        // Activity log entries are always single MVR lookups
        setMode('single');

        // Set VIN from log
        if (logDetail.vin) {
          setVin(logDetail.vin);
          setSearchedVin(logDetail.vin.toUpperCase().trim());
          handleVINValidation(logDetail.vin);
        }

        // Set state from log
        if (logDetail.stateCode) {
          setSelectedState(logDetail.stateCode);
          setSearchedState(logDetail.stateCode);
        }

        // Set record type from log (title, registration, both)
        if (logDetail.recordType === 'title') {
          setRecordType('title');
        } else if (logDetail.recordType === 'registration') {
          setRecordType('registration');
        } else if (logDetail.recordType === 'both') {
          setRecordType('both');
        }

        // Parse and transform the stored response
        // Handle both PascalCase (legacy stored data) and camelCase (new stored data)
        const rawData = JSON.parse(logDetail.responseBody);
        const parsedData = normalizeToCamelCase(rawData);
        const stateCode = logDetail.stateCode || '';

        // Clear both before loading to prevent stale data from a previous search
        setTitleData(null);
        setRegistrationData(null);

        // Pin replay to the report row the log was created from, so the PDF always
        // matches the live data captured at search time instead of falling through
        // to the backend's VIN+state fallback (which can return a different row).
        const pinnedReportId = logDetail.reportId ?? undefined;

        if (logDetail.recordType === 'title') {
          const transformedTitle = transformStoredTitleResponse(parsedData, stateCode);
          if (pinnedReportId) transformedTitle.reportId = pinnedReportId;
          setTitleData(transformedTitle);
          setActiveTab('title');
        } else if (logDetail.recordType === 'registration') {
          const transformedReg = transformStoredRegistrationResponse(parsedData, stateCode);
          if (pinnedReportId) transformedReg.reportId = pinnedReportId;
          setRegistrationData(transformedReg);
          setActiveTab('registration');
        } else if (logDetail.recordType === 'both') {
          // Combined response has nested titleResponse and registrationResponse
          if (parsedData.titleResponse) {
            const transformedTitle = transformStoredTitleResponse(parsedData.titleResponse, stateCode);
            if (pinnedReportId) transformedTitle.reportId = pinnedReportId;
            setTitleData(transformedTitle);
          }
          if (parsedData.registrationResponse) {
            const transformedReg = transformStoredRegistrationResponse(parsedData.registrationResponse, stateCode);
            if (pinnedReportId) transformedReg.reportId = pinnedReportId;
            setRegistrationData(transformedReg);
          }
          setActiveTab('title');
        }
      } catch (err) {
        console.error('Failed to load MVR from log:', err);
        setLogLoadError('Failed to load previous search results');
      } finally {
        setIsLoadingFromLog(false);
      }
    };

    loadFromLog();
  }, [searchParams]);

  /**
   * The API names the state a record moved to ("Ohio"); every connectivity check and the state
   * picker speak in codes. Resolve one to the other, case-insensitively, and return null rather
   * than guessing if the name is not one we know.
   */
  const stateCodeForName = (stateName: string): string | null => {
    const target = stateName.trim().toLowerCase();
    const match = Object.entries(stateNames).find(([, name]) => name.toLowerCase() === target);
    return match ? match[0] : null;
  };

  /**
   * Re-run the same VIN against the state that actually holds the record now. The VIN is
   * unchanged, so the search is pre-filled rather than executed — the next lookup is billable
   * and that is the user's decision to make, not ours.
   */
  const handleSearchMovedState = (stateCode: string) => {
    setSelectedState(stateCode);
    setNotice(null);
    setNoticeState(null);
    setNoticeStateCode(null);
    setError(null);
    setTitleData(null);
    setRegistrationData(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const isStateConnected = (stateCode: string): boolean => {
    // Check if state is in the dynamically fetched supported states list
    if (supportedStatesLoading || supportedStates.length === 0) {
      // Fall back to cached check while loading
      return isStateSupported(stateCode);
    }
    const stateName = stateNames[stateCode];
    if (!stateName) return false;
    return supportedStates.some(s => s.state === stateName);
  };

  // Handle VIN validation - quick validation first for instant preview, then full decode in background
  const handleVINValidation = async (vinValue: string) => {
    setVinValidationError('');
    setVinDecodeResult(null);

    if (!vinValue || vinValue.length < 5) return;

    const invalidChars = /[IOQ]/i;
    if (invalidChars.test(vinValue)) {
      setVinValidationError('Invalid VIN format. VIN cannot contain I, O, or Q.');
      return;
    }

    setIsValidatingVIN(true);
    try {
      // Step 1: Quick validation for instant preview (local decoder, no API calls)
      const quickResponse = await validateVINQuick(vinValue);
      if (quickResponse.decodeResult) {
        setVinDecodeResult(quickResponse.decodeResult);
      }
      if (!quickResponse.isValid && quickResponse.errorCode) {
        setVinValidationError(getVINErrorMessage(quickResponse.errorCode));
        setIsValidatingVIN(false);
        return;
      }

      setIsValidatingVIN(false);

      // Quick validation is sufficient for MVR - no background DataOne call
      // (DataOne calls are billable and not needed for MVR workflow)
    } catch (err) {
      console.error('VIN validation error:', err);
      setVinValidationError('Unable to validate VIN. Please try again.');
      setIsValidatingVIN(false);
    }
  };

  // Handle VIN input change
  const handleVINChange = (value: string) => {
    const upperValue = value.replace(/\s/g, '').toUpperCase();
    setVin(upperValue);

    if (vinValidationError) {
      setVinValidationError('');
    }

    if (upperValue.length === 17) {
      handleVINValidation(upperValue);
    } else if (upperValue.length < 5) {
      setVinDecodeResult(null);
    }
  };

  // A standard VIN is 17 characters, but some older vehicles have shorter ones. Ask before a paid
  // search goes out with one, so a typo is caught and a genuine old VIN is still allowed.
  const requestSearch = () => {
    if (vin && vin.length >= 5 && vin.length !== 17) {
      setShowShortVinPrompt(true);
      return;
    }
    handleSearch();
  };

  const handleSearch = async () => {
    if (!vin || vin.length < 5) {
      setError('Please enter a valid VIN (minimum 5 characters)');
      return;
    }

    if (!selectedState) {
      setError('Please select a state');
      return;
    }

    if (!isStateConnected(selectedState)) {
      setError('DMV connection is not available for the selected state');
      return;
    }

    setLoading(true);
    setError(null);
    setNotice(null);
    setNoticeState(null);
    setNoticeStateCode(null);
    setTitleData(null);
    setRegistrationData(null);

    try {
      const { titleResponse, registrationResponse } = await searchMVR(selectedState, vin, effectiveRecordType);

      // Pin the displayed report to the params we actually searched.
      setSearchedVin(vin.toUpperCase().trim());
      setSearchedState(selectedState);

      if (titleResponse) {
        if (!titleResponse.success && titleResponse.errorMessage) {
          // Only show error if both failed (or if we only searched one type)
          if (!registrationResponse?.success || effectiveRecordType === 'title') {
            setError(titleResponse.errorMessage);
          }
        }
        setTitleData(titleResponse);
      }

      if (registrationResponse) {
        if (!registrationResponse.success && registrationResponse.errorMessage) {
          // Show registration error if we only searched registration
          if (effectiveRecordType === 'registration') {
            setError(registrationResponse.errorMessage);
          }
        }
        setRegistrationData(registrationResponse);
      }

      // A state may answer without failing — surface that instead of an empty result.
      const answered = titleResponse?.statusMessage || registrationResponse?.statusMessage;
      if (answered) {
        const movedTo = titleResponse?.recordMovedToState || registrationResponse?.recordMovedToState || null;
        setNotice(answered);
        setNoticeState(movedTo);
        setNoticeStateCode(movedTo ? stateCodeForName(movedTo) : null);
      }

      // Default to appropriate tab based on recordType selection and data availability
      if (effectiveRecordType === 'title') {
        setActiveTab('title');
      } else if (effectiveRecordType === 'registration') {
        setActiveTab('registration');
      } else if (titleResponse?.titles?.length) {
        setActiveTab('title');
      } else if (registrationResponse?.registrations?.length) {
        setActiveTab('registration');
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to retrieve motor vehicle records';
      setError(getErrorMessage(err) || errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setVin('');
    setSelectedState('');
    setSearchedVin('');
    setSearchedState('');
    setError(null);
    setTitleData(null);
    setRegistrationData(null);
    setVinDecodeResult(null);
    setVinValidationError('');
    setPdfPreviewError('');
    clearMVRSession();
  };

  // Bulk mode handlers
  const handleModeChange = (newMode: 'single' | 'bulk') => {
    setMode(newMode);
    setError(null);
    setBulkError('');
  };

  const handleBulkSearch = async (items: BulkMvrItem[]) => {
    setBulkError('');
    setIsBulkSearching(true);
    setBulkResults(null);
    try {
      const response = await createBulkMvrReports(items);
      setBulkResults(response);
    } catch (err) {
      setBulkError(getErrorMessage(err));
    } finally {
      setIsBulkSearching(false);
    }
  };

  const handleGenerateBulkPdf = async () => {
    if (!bulkResults || bulkResults.totalSucceeded === 0) return;
    setIsGeneratingBulkPdf(true);
    setBulkError('');
    try {
      const requestedBy = user?.firstName && user?.lastName
        ? `${user.firstName} ${user.lastName}` : user?.email;
      const items = bulkResults.results
        .filter(r => r.success)
        .map(r => ({
          vin: r.vin,
          stateCode: r.stateCode,
          recordType: r.recordType as MvrRecordType,
          referenceNote: r.referenceNote,
        }));
      await generateBulkMvrPdf(items, requestedBy || undefined);
    } catch (err) {
      setBulkError(getErrorMessage(err));
    } finally {
      setIsGeneratingBulkPdf(false);
    }
  };

  const handleClearBulk = () => {
    setBulkResults(null);
    setBulkError('');
    sessionStorage.removeItem(MVR_SESSION_KEYS.BULK_RESULTS);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      requestSearch();
    }
  };

  // Auto-fetch the embedded MVR PDF whenever a successful single-mode search yields data.
  // Idaho legacy uses POST /api/idahop2p/generate-pdf; everything else uses
  // GET /api/state-dmv/{state}/mvr-pdf — fetchMvrPdfBlob picks the right one.
  useEffect(() => {
    if (mode !== 'single') return;
    if (!searchedState || !searchedVin) return;
    const titleId = titleData?.reportId;
    const regId = registrationData?.reportId;
    if (!titleId && !regId) return;

    let cancelled = false;
    let createdUrl: string | null = null;

    setPdfPreviewLoading(true);
    setPdfPreviewError('');

    const requestorName = user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : user?.email;

    fetchMvrPdfBlob({
      vin: searchedVin,
      stateCode: searchedState,
      titleReportId: (effectiveRecordType === 'title' || effectiveRecordType === 'both') ? (titleId || undefined) : undefined,
      registrationReportId: (effectiveRecordType === 'registration' || effectiveRecordType === 'both') ? (regId || undefined) : undefined,
      requestedBy: requestorName || undefined,
      recordType: effectiveRecordType,
      cylinders: vinDecodeResult?.engine?.cylinders,
      fuelType: vinDecodeResult?.engine?.fuelType,
      engineDisplacement: vinDecodeResult?.engine?.displacementL,
      horsepower: vinDecodeResult?.engine?.horsepower,
      transmissionStyle: vinDecodeResult?.transmission?.style,
      driveType: vinDecodeResult?.driveType,
    })
      .then((blob) => {
        if (cancelled) return;
        createdUrl = window.URL.createObjectURL(blob);
        setPdfPreviewUrl((prev) => {
          if (prev) window.URL.revokeObjectURL(prev);
          return createdUrl;
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setPdfPreviewError(getErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setPdfPreviewLoading(false);
      });

    return () => {
      cancelled = true;
      if (createdUrl) {
        window.URL.revokeObjectURL(createdUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, searchedState, searchedVin, titleData?.reportId, registrationData?.reportId, effectiveRecordType]);

  // Reset the embedded PDF when the user clears results or switches modes.
  useEffect(() => {
    if (mode !== 'single' || (!titleData && !registrationData)) {
      setPdfPreviewUrl((prev) => {
        if (prev) window.URL.revokeObjectURL(prev);
        return null;
      });
      setPdfPreviewError('');
    }
  }, [mode, titleData, registrationData]);

  const handlePrintPdf = () => {
    const iframeWindow = pdfIframeRef.current?.contentWindow;
    if (!iframeWindow) return;
    try {
      iframeWindow.focus();
      iframeWindow.print();
    } catch (err) {
      console.error('Failed to invoke print on embedded PDF', err);
    }
  };

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#c41e3a] animate-spin" />
      </div>
    );
  }

  // Don't render if not authenticated (will redirect)
  if (!user) {
    return null;
  }

  const connectedCount = coverageData?.dmv.total || 0;
  const hasResults = titleData || registrationData;
  // A lookup can complete successfully but return zero records (e.g. the state has no
  // record for this VIN). The response objects exist but their arrays are empty — that's
  // "No records on file", distinct from a hard failure (handled by `error`).
  const hasAnyRecords =
    (titleData?.titles?.length ?? 0) > 0 ||
    (registrationData?.registrations?.length ?? 0) > 0;
  const currentData = activeTab === 'title' ? titleData : registrationData;
  // True when the inputs no longer match what produced the displayed results, so we can
  // warn the user that the shown report is from the previous search (not the new inputs).
  const inputsChangedSinceSearch = !!hasResults &&
    (vin.trim().toUpperCase() !== searchedVin || selectedState !== searchedState);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0] text-[#0f172a]">
      {/* Subtle Brand Ambient Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-1/4 w-[800px] h-[800px] bg-[radial-gradient(ellipse_at_center,rgba(196,30,58,0.04)_0%,transparent_70%)] rounded-full"></div>
        <div className="absolute bottom-0 left-1/4 w-[600px] h-[600px] bg-[radial-gradient(ellipse_at_center,rgba(196,30,58,0.03)_0%,transparent_70%)] rounded-full"></div>
      </div>

      <Navigation onMenuClick={() => setIsSidebarOpen(true)} />
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <main className="relative z-10 lg:ml-[220px] pt-[88px] flex-1">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          {/* Page Header */}
          <div className="glass-card-strong p-6 rounded-xl mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[#c41e3a] text-xs font-bold uppercase tracking-wider">Motor Vehicle Record</span>
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-[#0f172a] mb-1">
                  Motor Vehicle Record
                </h1>
                <p className="text-[#64748b] text-sm">
                  Access comprehensive motor vehicle records and registration history
                  {!coverageLoading && connectedCount > 0 && (
                    <span className="ml-2 text-[#3b82f6]">
                      ({connectedCount} state{connectedCount !== 1 ? 's' : ''} connected)
                    </span>
                  )}
                </p>
              </div>
              {currentData && currentData.reportId && (
                <div className="flex items-center gap-4 glass-card px-4 py-3 rounded-lg flex-shrink-0">
                  <div className="text-right">
                    <div className="text-[10px] text-[#64748b] uppercase tracking-wider whitespace-nowrap">Report ID</div>
                    <div className="font-mono text-sm font-semibold text-[#0f172a] whitespace-nowrap">{getShortReportId(currentData.reportId)}</div>
                  </div>
                  <div className="w-px h-8 bg-black/10"></div>
                  <div className="text-right">
                    <div className="text-[10px] text-[#64748b] uppercase tracking-wider whitespace-nowrap">Generated</div>
                    <div className="text-sm font-medium text-[#0f172a] whitespace-nowrap">{formatMvrDate(currentData.fetchedAt)}</div>
                  </div>
                  <div className="w-px h-8 bg-black/10"></div>
                  <div className="flex items-center gap-1.5 text-[#16a34a] whitespace-nowrap">
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                    <span className="text-sm font-semibold">Verified</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Mode Toggle Tabs */}
          <div data-tour="mvr-search" className="glass-card-strong rounded-xl mb-6 overflow-hidden">
            <div className="flex border-b border-black/[0.06]">
              <button
                onClick={() => handleModeChange('single')}
                disabled={loading || isBulkSearching}
                className={`flex-1 px-6 py-3 text-sm font-medium transition-all ${
                  mode === 'single'
                    ? 'bg-white/80 text-[#0f172a] border-b-2 border-[#c41e3a]'
                    : 'text-[#64748b] hover:text-[#0f172a] hover:bg-white/40'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                Single VIN
              </button>
              <button
                onClick={() => handleModeChange('bulk')}
                disabled={loading || isBulkSearching}
                className={`flex-1 px-6 py-3 text-sm font-medium transition-all ${
                  mode === 'bulk'
                    ? 'bg-white/80 text-[#0f172a] border-b-2 border-[#c41e3a]'
                    : 'text-[#64748b] hover:text-[#0f172a] hover:bg-white/40'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                Bulk VINs
              </button>
            </div>

            {/* Single VIN Search Form */}
            {mode === 'single' && (
              <div className="p-4">
                <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-3">
                  {/* VIN Input */}
              <div className="flex-1 flex items-center gap-3 bg-white rounded-lg px-4 py-3 border border-black/[0.08]">
                <Search className="w-5 h-5 text-[#94a3b8]" />
                <input
                  type="text"
                  value={vin}
                  onChange={(e) => handleVINChange(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Enter VIN"
                  disabled={loading}
                  className="flex-1 bg-transparent text-[#0f172a] font-mono text-sm tracking-wider placeholder-[#94a3b8] focus:outline-none disabled:opacity-50"
                />
                <div>
                  {loading || isValidatingVIN ? (
                    <Loader2 className="w-5 h-5 text-[#c41e3a] animate-spin" />
                  ) : vinDecodeResult && !vinValidationError ? (
                    <CheckCircle2 className="w-5 h-5 text-[#16a34a]" />
                  ) : vinValidationError ? (
                    <AlertCircle className="w-5 h-5 text-[#d97706]" />
                  ) : null}
                </div>
              </div>

              {/* State Dropdown */}
              <div className="relative min-w-[200px]">
                <div className="flex items-center gap-3 bg-white rounded-lg px-4 py-3 border border-black/[0.08]">
                  <MapPin className="w-5 h-5 text-[#94a3b8]" />
                  <select
                    value={selectedState}
                    onChange={(e) => setSelectedState(e.target.value)}
                    disabled={loading}
                    className="flex-1 bg-transparent text-[#0f172a] text-sm focus:outline-none appearance-none cursor-pointer pr-6 disabled:opacity-50"
                  >
                    <option value="">Select State</option>
                    {allStateCodes
                      .filter((code) => isStateConnected(code))
                      .map((code) => (
                        <option key={code} value={code}>
                          {stateNames[code]} ({code})
                        </option>
                      ))}
                  </select>
                  <ChevronDown className="w-4 h-4 text-[#94a3b8] absolute right-4 pointer-events-none" />
                </div>
              </div>

              {/* Record Type Toggle - Hidden for single-price states and combined-search states without separate pricing */}
              {!isCombinedState ? (
                <div className="flex items-center bg-white rounded-lg border border-black/[0.08] p-1">
                  <button
                    type="button"
                    onClick={() => setRecordType('title')}
                    disabled={loading || !stateSupportsTitle}
                    title={!stateSupportsTitle ? 'This state does not return title records' : undefined}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      recordType === 'title'
                        ? 'bg-[#c41e3a] text-white'
                        : 'text-[#64748b] hover:text-[#0f172a] hover:bg-gray-100'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    Title
                  </button>
                  <button
                    type="button"
                    onClick={() => setRecordType('registration')}
                    disabled={loading || !stateSupportsRegistration || !registrationAloneOffered}
                    title={!stateSupportsRegistration
                      ? 'This state does not return registration records'
                      : !registrationAloneOffered
                        ? 'This state sells registration only with the title: choose Title & Registration'
                        : undefined}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      recordType === 'registration'
                        ? 'bg-[#c41e3a] text-white'
                        : 'text-[#64748b] hover:text-[#0f172a] hover:bg-gray-100'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    Registration
                  </button>
                  <button
                    type="button"
                    onClick={() => setRecordType('both')}
                    disabled={loading || !(stateSupportsTitle && stateSupportsRegistration)}
                    title={!(stateSupportsTitle && stateSupportsRegistration) ? 'This state only returns one record type' : undefined}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      recordType === 'both'
                        ? 'bg-[#c41e3a] text-white'
                        : 'text-[#64748b] hover:text-[#0f172a] hover:bg-gray-100'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    Both
                  </button>
                </div>
              ) : (
                <div className="flex items-center bg-white/60 rounded-lg border border-black/[0.08] px-4 py-2">
                  <span className="text-sm text-[#64748b]">{combinedLabel}</span>
                </div>
              )}

              {/* Estimated Cost */}
              {estimatedCost && selectedState && (
                <span className="badge-success px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap">
                  {estimatedCost.totalCost === 0
                    ? 'No Charge'
                    : `Est. $${estimatedCost.totalCost.toFixed(2)}`}
                </span>
              )}

              {/* Buttons */}
              <button
                onClick={requestSearch}
                disabled={loading || coverageLoading || !vin}
                className="btn-primary px-6 py-3 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {loading ? 'Searching...' : 'Search'}
              </button>
              {(vin || selectedState || hasResults) && (
                <button
                  onClick={handleClear}
                  disabled={loading}
                  className="btn-secondary px-5 py-3 disabled:opacity-50 whitespace-nowrap"
                >
                  Clear
                </button>
              )}
                </div>

              {/* DPPA permissible use — read-only, from client profile */}
              {dppaDisplay && (
                <div className="mt-3 flex items-center gap-2.5 px-4 py-2.5 rounded-lg bg-white border border-black/[0.08] text-sm text-[#475569]">
                  <svg className="w-4 h-4 text-[#94a3b8] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                  <span className="font-medium text-[#0f172a]">DPPA Permissible Use:</span>
                  <span>{dppaDisplay}</span>
                </div>
              )}
              </div>
            )}

            {/* Bulk VIN Input */}
            {mode === 'bulk' && (
              <div className="p-4">
                <BulkMVRInput
                  onSubmit={handleBulkSearch}
                  disabled={isBulkSearching}
                  maxItems={50}
                  supportedStates={supportedStates}
                  showPricing={showPricing}
                />
              {clientDppa && (
                <div className="mt-3 flex items-center gap-2.5 px-4 py-2.5 rounded-lg bg-white border border-black/[0.08] text-sm text-[#475569]">
                  <svg className="w-4 h-4 text-[#94a3b8] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                  <span className="font-medium text-[#0f172a]">DPPA Permissible Use:</span>
                  <span>{dppaDisplay}</span>
                </div>
              )}
              </div>
            )}
          </div>

          {/* Bulk mode error */}
          {mode === 'bulk' && bulkError && (
            <div className="mb-6 p-4 bg-[#dc2626]/10 border border-[#dc2626]/30 rounded-xl text-[#dc2626] text-sm flex items-start gap-3">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <p>{bulkError}</p>
            </div>
          )}

          {/* Bulk Searching State */}
          {mode === 'bulk' && isBulkSearching && (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <Loader2 className="w-16 h-16 text-[#c41e3a] animate-spin mx-auto mb-4" />
                <h2 className="text-xl text-[#0f172a] mb-2">Processing Bulk Request...</h2>
                <p className="text-[#64748b]">Querying DMV records for multiple vehicles</p>
              </div>
            </div>
          )}

          {/* Bulk Results */}
          {mode === 'bulk' && !isBulkSearching && bulkResults && (
            <div className="space-y-6">
              <BulkMVRResults
                results={bulkResults}
                showPricing={showPricing}
                onGeneratePdf={handleGenerateBulkPdf}
                isGeneratingPdf={isGeneratingBulkPdf}
              />
              <div className="flex justify-center">
                <button
                  onClick={handleClearBulk}
                  className="btn-secondary px-6 py-2"
                >
                  Clear Results
                </button>
              </div>
            </div>
          )}

          {/* State told us something on a successful lookup (single mode) — not an error */}
          {mode === 'single' && notice && (
            <div className="mb-6 p-4 bg-[#3b82f6]/10 border border-[#3b82f6]/30 rounded-xl text-sm">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5 text-[#3b82f6]" />
                <div>
                  <p className="font-medium text-[#0f172a]">{notice}</p>
                  {noticeStateCode && isStateConnected(noticeStateCode) && (
                    <button
                      type="button"
                      onClick={() => handleSearchMovedState(noticeStateCode)}
                      className="mt-2 text-[#3b82f6] underline underline-offset-2 hover:text-[#2563eb]"
                    >
                      Search {noticeState} for this VIN
                    </button>
                  )}
                  {noticeState && !(noticeStateCode && isStateConnected(noticeStateCode)) && (
                    <p className="text-xs text-[#64748b] mt-1">
                      {noticeState} is not currently available through AutoAccess.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Non-standard VIN length: confirm before searching */}
          {showShortVinPrompt && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
              <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
                <h3 className="text-lg font-semibold text-[#0f172a] mb-2">VIN is not 17 characters</h3>
                <p className="text-sm text-[#475569] mb-5">
                  The VIN you entered is {vin.length} characters. A standard VIN is 17, though some older
                  vehicles have shorter ones. Do you want to search with this VIN anyway?
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowShortVinPrompt(false)}
                    className="px-4 py-2 text-sm rounded-lg border border-black/[0.12] text-[#475569] hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowShortVinPrompt(false);
                      handleSearch();
                    }}
                    className="btn-primary px-4 py-2 text-sm"
                  >
                    Search anyway
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Error Display (single mode) */}
          {mode === 'single' && error && (
            <div className="mb-6 p-4 bg-[#dc2626]/10 border border-[#dc2626]/30 rounded-xl text-[#dc2626] text-sm flex items-start gap-3">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <p>{error}</p>
            </div>
          )}

          {/* VIN Validation Warning (single mode) */}
          {mode === 'single' && vinValidationError && (
            <div className="mb-6 p-4 bg-[#d97706]/10 border border-[#d97706]/30 rounded-xl text-sm">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-[#d97706]" />
                <div>
                  <p className="font-medium text-[#d97706]">{vinValidationError}</p>
                  <p className="text-xs text-[#92400e] mt-1">
                    You can still search this VIN. The DMV may return data with exceptions.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Vehicle Info Card (shows immediately when VIN is validated) - single mode only */}
          {mode === 'single' && vinDecodeResult && !vinValidationError && (
            <div className="mb-6 glass-card p-4 rounded-xl">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#c41e3a]/10 flex items-center justify-center">
                  <Car className="w-5 h-5 text-[#c41e3a]" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-[#0f172a]">{formatVehicleInfo(vinDecodeResult)}</p>
                    <div className="flex items-center gap-3">
                      {vinDecodeResult.checkDigitValid && (
                        <span className="badge-success px-2 py-1 rounded text-xs font-medium">Check Digit Valid</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-sm text-[#64748b]">
                    <span>VIN: <span className="font-mono text-[#c41e3a]">{vin}</span></span>
                    {vinDecodeResult.bodyClass && <span>| {vinDecodeResult.bodyClass}</span>}
                    {vinDecodeResult.driveType && <span>| {vinDecodeResult.driveType}</span>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Searching/Loading State - single mode only */}
          {mode === 'single' && (loading || isLoadingFromLog) && (
            <div className="glass-card p-12 rounded-xl">
              <div className="text-center">
                <Loader2 className="w-16 h-16 text-[#c41e3a] animate-spin mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-[#475569] mb-2">
                  {isLoadingFromLog ? 'Loading Previous Results...' : 'Searching...'}
                </h2>
                <p className="text-[#64748b]">
                  {isLoadingFromLog ? 'Restoring your previous search' : 'Querying motor vehicle records'}
                </p>
              </div>
            </div>
          )}

          {/* Log Load Error - single mode only */}
          {mode === 'single' && logLoadError && !loading && !isLoadingFromLog && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-red-800 font-medium">{logLoadError}</p>
                  <p className="text-xs text-red-600 mt-1">
                    You can search again using the form above.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Results State - single mode only */}
          {mode === 'single' && !loading && !isLoadingFromLog && hasResults && (
            <div className="space-y-6">

              {/* Stale-input warning: inputs changed since the last search, so the report
                  below is for the PREVIOUS search — not what's currently in the boxes. */}
              {inputsChangedSinceSearch && (
                <div className="p-4 bg-[#d97706]/10 border border-[#d97706]/30 rounded-xl text-sm">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-[#d97706]" />
                    <div>
                      <p className="font-medium text-[#d97706]">
                        You've changed the VIN or state — these results are still for your previous search
                        ({searchedVin} · {stateNames[searchedState] || searchedState}).
                      </p>
                      <p className="text-xs text-[#92400e] mt-1">
                        Click <span className="font-semibold">Search</span> to run a new lookup for the values you just entered.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {!hasAnyRecords ? (
                /* Lookup completed but the state returned no records for this VIN. */
                <div className="text-center py-12 glass-card rounded-xl">
                  <Car className="w-12 h-12 mx-auto mb-3 text-[#94a3b8]" />
                  <p className="text-[#0f172a] font-semibold">No records on file for this VIN</p>
                  <p className="text-[#64748b] text-sm mt-1">
                    The lookup completed and {stateNames[searchedState] || searchedState} accepted this VIN, but it has no lien, sale or title records on file for VIN {searchedVin}.
                  </p>
                </div>
              ) : (
                <>
                  {/* Per-type placeholder when one requested type came back empty but the other has data */}
                  {effectiveRecordType === 'both' && !(titleData?.titles?.length) && (
                    <div className="text-center py-8 glass-card rounded-xl">
                      <FileText className="w-10 h-10 mx-auto mb-2 text-[#94a3b8]" />
                      <p className="text-[#64748b] text-sm">No title records found for this VIN</p>
                    </div>
                  )}
                  {effectiveRecordType === 'both' && !(registrationData?.registrations?.length) && (
                    <div className="text-center py-8 glass-card rounded-xl">
                      <Car className="w-10 h-10 mx-auto mb-2 text-[#94a3b8]" />
                      <p className="text-[#64748b] text-sm">No registration records found for this VIN</p>
                    </div>
                  )}

                  {/* Embedded PDF preview */}
                  <div className="glass-card-strong rounded-xl overflow-hidden">
                    {pdfPreviewLoading ? (
                      <div className="flex items-center justify-center py-16">
                        <Loader2 className="w-8 h-8 text-[#c41e3a] animate-spin mr-3" />
                        <span className="text-[#64748b] text-sm">Loading PDF preview...</span>
                      </div>
                    ) : pdfPreviewError ? (
                      <div className="flex items-center justify-center py-12 px-4">
                        <AlertCircle className="w-5 h-5 text-[#d97706] mr-2 flex-shrink-0" />
                        <span className="text-[#92400e] text-sm">{pdfPreviewError}</span>
                      </div>
                    ) : pdfPreviewUrl ? (
                      <iframe
                        ref={pdfIframeRef}
                        src={`${pdfPreviewUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                        title="Motor Vehicle Record PDF"
                        className="w-full border-0 bg-white"
                        style={{ height: 'min(1500px, 90vh)' }}
                      />
                    ) : null}
                  </div>

                  {/* DPPA Disclaimer */}
                  <div className="glass-card p-4 rounded-xl">
                    <p className="text-xs text-[#64748b] italic leading-relaxed">
                      Vehicle records provided through AutoAccess are obtained directly from the applicable state motor vehicle authority, based on data available on the date of retrieval. Personal information contained in driver and vehicle records is protected under the Driver Privacy Protection Act (DPPA). Unauthorized use or disclosure of this information may result in civil and criminal liability as provided by law. Acceptance of these records may vary by state or jurisdiction.
                    </p>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Idle State - single mode only */}
          {mode === 'single' && !loading && !hasResults && !error && (
            <div className="glass-card p-12 rounded-xl">
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-[#c41e3a]/10 flex items-center justify-center">
                  <FileText className="w-10 h-10 text-[#c41e3a]" />
                </div>
                <h2 className="text-xl font-semibold text-[#475569] mb-2">
                  Search for Motor Vehicle Records
                </h2>
                <p className="text-[#64748b] max-w-md mx-auto">
                  Enter a VIN and select a state to retrieve comprehensive motor vehicle records
                  including registration history, title information, and more.
                </p>
                {!coverageLoading && connectedCount > 0 && (
                  <p className="text-[#3b82f6] mt-4 text-sm">
                    Currently connected to {connectedCount} state DMV{connectedCount !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />

      {/* Floating Print PDF Button - single mode only */}
      {mode === 'single' && hasResults && !loading && pdfPreviewUrl && (
        <button
          onClick={handlePrintPdf}
          className="fixed bottom-8 right-8 px-6 py-4 bg-gradient-to-r from-[#c41e3a] to-[#a01830] hover:from-[#a01830] hover:to-[#8a1428] text-white font-semibold rounded-full shadow-[0_8px_32px_rgba(196,30,58,0.35)] transition-all flex items-center gap-2 z-50 hover:scale-105"
          title="Print embedded PDF"
        >
          <Printer className="w-5 h-5" />
          <span>Print PDF</span>
        </button>
      )}
    </div>
  );
};

export default MotorVehicleRecord;
